/**
 * encounterTimeline — pure year-grouping for the Encounters timeline. The server returns the page already
 * date-DESC (ORDER BY period_start_date_key DESC), so this preserves that order: it walks the rows once,
 * derives the YEAR off the REAL period_start_date_key (raw YYYYMMDD int) with a fallback to the formatted
 * period_start string, and buckets contiguous rows under their year in first-seen (newest-first) order.
 * A row with no recoverable date lands in an honest "Unknown" bucket (never silently dropped). This only
 * groups the CURRENT page; the true cross-page count comes from meta.total, shown separately.
 */
import type { Encounter } from "@shared/contracts";

/** Sentinel year label for a row whose date cannot be recovered from either real column. */
export const UNKNOWN_YEAR = "Unknown";

export interface YearGroup {
  year: string; // "2025" | "Unknown"
  count: number; // rows on THIS page for the year (not the cross-page total)
  rows: Encounter[];
}

/** Derive the 4-digit year from the raw YYYYMMDD key, else the YYYY-MM-DD string, else UNKNOWN_YEAR. */
export function encounterYear(row: Pick<Encounter, "period_start_date_key" | "period_start">): string {
  const key = row.period_start_date_key;
  if (typeof key === "number" && Number.isFinite(key) && key >= 10000000) {
    return String(Math.floor(key / 10000));
  }
  const s = row.period_start;
  if (typeof s === "string" && /^\d{4}/.test(s)) return s.slice(0, 4);
  return UNKNOWN_YEAR;
}

/** Group date-DESC rows by year, preserving newest-first order; counts are per-page (see meta.total). */
export function groupByYear(rows: Encounter[]): YearGroup[] {
  const groups: YearGroup[] = [];
  const byYear = new Map<string, YearGroup>();
  for (const row of rows) {
    const year = encounterYear(row);
    const existing = byYear.get(year);
    if (existing) {
      existing.rows.push(row);
      existing.count += 1;
    } else {
      const group: YearGroup = { year, count: 1, rows: [row] };
      byYear.set(year, group);
      groups.push(group);
    }
  }
  return groups;
}
